/**
 * Pairing Manager
 *
 * Orchestrates phone ↔ reader pairing via Supabase Realtime Broadcast.
 * Handles handshake, text transfer, transport commands, and acknowledgements.
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  createPairingChannel,
  broadcastMessage,
  onBroadcastMessage,
  subscribeChannel,
  removeChannel,
} from './supabaseClient';
import {
  type Role,
  type TransportCommand,
  type HelloMessage,
  type AckMessage,
  HELLO_TIMEOUT_MS,
  ACK_TIMEOUT_MS,
  // Guards
  isHelloMessage,
  isHelloAckMessage,
  isSetTextMessage,
  isSetTextAckMessage,
  isSetTextBeginMessage,
  isSetTextChunkMessage,
  isSetTextEndMessage,
  isSetTextChunkedAckMessage,
  isPlayMessage,
  isPauseMessage,
  isRewindMessage,
  isFastForwardMessage,
  isRestartMessage,
  isTransportAckMessage,
  isSetWpmMessage,
  isSetWpmAckMessage,
  // Factories
  createHelloMessage,
  createHelloAckMessage,
  createSetTextMessage,
  createSetTextAckMessage,
  createSetTextBeginMessage,
  createSetTextChunkMessage,
  createSetTextEndMessage,
  createSetTextChunkedAckMessage,
  createPlayMessage,
  createPauseMessage,
  createRewindMessage,
  createFastForwardMessage,
  createRestartMessage,
  createTransportAckMessage,
  createSetWpmMessage,
  createSetWpmAckMessage,
  generateTransferId,
} from './protocol';
import {
  getUtf8ByteLength,
  needsChunking,
  chunkText,
  ChunkCollector,
} from './chunking';

// =============================================================================
// Types
// =============================================================================

/** Pairing connection state */
export type PairingState = 'disconnected' | 'connecting' | 'waiting' | 'connected';

/** Event types emitted by PairingManager */
export type PairingEventType =
  | 'stateChange'
  | 'textReceived'
  | 'textSent'
  | 'commandReceived'
  | 'commandSent'
  | 'wpmReceived'
  | 'wpmSent'
  | 'error'
  | 'transferProgress';

/** Base event interface */
interface BasePairingEvent {
  type: PairingEventType;
}

export interface StateChangeEvent extends BasePairingEvent {
  type: 'stateChange';
  state: PairingState;
}

export interface TextReceivedEvent extends BasePairingEvent {
  type: 'textReceived';
  text: string;
  bytes: number;
}

export interface TextSentEvent extends BasePairingEvent {
  type: 'textSent';
  id: string;
  bytes: number;
}

export interface CommandReceivedEvent extends BasePairingEvent {
  type: 'commandReceived';
  command: TransportCommand;
  words?: number;
}

export interface CommandSentEvent extends BasePairingEvent {
  type: 'commandSent';
  command: TransportCommand;
}

export interface WpmReceivedEvent extends BasePairingEvent {
  type: 'wpmReceived';
  wpm: number;
}

export interface WpmSentEvent extends BasePairingEvent {
  type: 'wpmSent';
  wpm: number;
}

export interface ErrorEvent extends BasePairingEvent {
  type: 'error';
  error: string;
}

export interface TransferProgressEvent extends BasePairingEvent {
  type: 'transferProgress';
  received: number;
  total: number;
}

export type PairingEvent =
  | StateChangeEvent
  | TextReceivedEvent
  | TextSentEvent
  | CommandReceivedEvent
  | CommandSentEvent
  | WpmReceivedEvent
  | WpmSentEvent
  | ErrorEvent
  | TransferProgressEvent;

/** Event listener callback */
export type PairingEventListener = (event: PairingEvent) => void;

// =============================================================================
// Pairing Manager
// =============================================================================

/**
 * Manages pairing session between phone and reader.
 */
export class PairingManager {
  private role: Role;
  private sessionCode: string;
  private channel: RealtimeChannel | null = null;
  private state: PairingState = 'disconnected';
  private listeners: Set<PairingEventListener> = new Set();

  // Pending ack tracking
  private pendingAcks: Map<string, { resolve: () => void; reject: (err: Error) => void; timeout: number }> = new Map();

  // Chunk collection for receiving chunked transfers
  private chunkCollector: ChunkCollector | null = null;
  private currentTransferId: string | null = null;

  /**
   * Create a new PairingManager.
   *
   * @param role - 'phone' or 'reader'
   * @param sessionCode - The pairing session code
   */
  constructor(role: Role, sessionCode: string) {
    this.role = role;
    this.sessionCode = sessionCode;
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  /** Get current pairing state */
  get currentState(): PairingState {
    return this.state;
  }

  /** Check if connected */
  get isConnected(): boolean {
    return this.state === 'connected';
  }

  // ---------------------------------------------------------------------------
  // Event Subscription
  // ---------------------------------------------------------------------------

  /**
   * Subscribe to pairing events.
   *
   * @param listener - Callback for events
   * @returns Unsubscribe function
   */
  on(listener: PairingEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: PairingEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (err) {
        console.error('Error in pairing event listener:', err);
      }
    }
  }

  private setState(newState: PairingState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.emit({ type: 'stateChange', state: newState });
    }
  }

  // ---------------------------------------------------------------------------
  // Connection
  // ---------------------------------------------------------------------------

  /**
   * Connect to the pairing channel and perform handshake.
   *
   * @returns Promise that resolves when connected
   */
  async connect(): Promise<void> {
    if (this.state !== 'disconnected') {
      throw new Error(`Cannot connect from state: ${this.state}`);
    }

    this.setState('connecting');

    try {
      // Create and configure channel
      this.channel = createPairingChannel(this.sessionCode);
      onBroadcastMessage(this.channel, this.handleMessage.bind(this));

      // Subscribe to channel
      await subscribeChannel(this.channel);

      // Perform handshake based on role
      if (this.role === 'reader') {
        // Reader waits for phone's hello
        this.setState('waiting');
      } else {
        // Phone sends hello and waits for ack
        this.setState('waiting');
        await this.sendHello();
      }
    } catch (err) {
      this.setState('disconnected');
      throw err;
    }
  }

  /**
   * Disconnect from the pairing channel.
   */
  async disconnect(): Promise<void> {
    // Clear pending acks
    for (const [key, pending] of this.pendingAcks) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Disconnected'));
      this.pendingAcks.delete(key);
    }

    // Remove channel
    if (this.channel) {
      await removeChannel(this.channel);
      this.channel = null;
    }

    this.chunkCollector = null;
    this.currentTransferId = null;
    this.setState('disconnected');
  }

  // ---------------------------------------------------------------------------
  // Handshake
  // ---------------------------------------------------------------------------

  private async sendHello(): Promise<void> {
    if (!this.channel) return;

    const helloMsg = createHelloMessage(this.role);
    await broadcastMessage(this.channel, helloMsg as unknown as Record<string, unknown>);

    // Wait for ack
    await this.waitForAck('hello', HELLO_TIMEOUT_MS);
  }

  private async handleHello(msg: HelloMessage): Promise<void> {
    if (!this.channel) return;

    // Only reader responds to phone's hello
    if (this.role === 'reader' && msg.role === 'phone') {
      // Send ack
      const ackMsg = createHelloAckMessage();
      await broadcastMessage(this.channel, ackMsg as unknown as Record<string, unknown>);
      this.setState('connected');
    }
  }

  // ---------------------------------------------------------------------------
  // Text Transfer (Phone → Reader)
  // ---------------------------------------------------------------------------

  /**
   * Send text to the connected peer (phone sends to reader).
   *
   * @param text - Text to send
   * @returns Promise that resolves when acknowledged
   */
  async sendText(text: string): Promise<void> {
    if (!this.isConnected || !this.channel) {
      throw new Error('Not connected');
    }

    const bytes = getUtf8ByteLength(text);
    const id = generateTransferId();

    if (needsChunking(text)) {
      await this.sendChunkedText(id, text, bytes);
    } else {
      await this.sendSmallText(id, text, bytes);
    }

    this.emit({ type: 'textSent', id, bytes });
  }

  private async sendSmallText(id: string, text: string, bytes: number): Promise<void> {
    if (!this.channel) return;

    const msg = createSetTextMessage(id, text, bytes);
    await broadcastMessage(this.channel, msg as unknown as Record<string, unknown>);

    await this.waitForAck(`setText:${id}`, ACK_TIMEOUT_MS);
  }

  private async sendChunkedText(id: string, text: string, bytes: number): Promise<void> {
    if (!this.channel) return;

    const { chunks, chunkCount } = chunkText(text);

    // Send begin
    const beginMsg = createSetTextBeginMessage(id, chunkCount, bytes);
    await broadcastMessage(this.channel, beginMsg as unknown as Record<string, unknown>);

    // Send chunks
    for (let i = 0; i < chunks.length; i++) {
      const chunkMsg = createSetTextChunkMessage(id, i, chunks[i]);
      await broadcastMessage(this.channel, chunkMsg as unknown as Record<string, unknown>);
    }

    // Send end
    const endMsg = createSetTextEndMessage(id);
    await broadcastMessage(this.channel, endMsg as unknown as Record<string, unknown>);

    // Wait for chunked ack
    await this.waitForAck(`setTextChunked:${id}`, ACK_TIMEOUT_MS);
  }

  private async handleSetText(id: string, text: string, bytes: number): Promise<void> {
    if (!this.channel) return;

    // Validate byte count
    const actualBytes = getUtf8ByteLength(text);
    if (actualBytes !== bytes) {
      this.emit({ type: 'error', error: `Byte count mismatch: got ${actualBytes}, expected ${bytes}` });
      return;
    }

    // Send ack
    const ackMsg = createSetTextAckMessage(id);
    await broadcastMessage(this.channel, ackMsg as unknown as Record<string, unknown>);

    // Emit received event
    this.emit({ type: 'textReceived', text, bytes });
  }

  private handleSetTextBegin(id: string, totalChunks: number, totalBytes: number): void {
    this.currentTransferId = id;
    this.chunkCollector = new ChunkCollector(totalChunks, totalBytes);
  }

  private handleSetTextChunk(id: string, index: number, chunk: string): void {
    if (this.currentTransferId !== id || !this.chunkCollector) {
      return;
    }

    this.chunkCollector.addChunk(index, chunk);

    // Emit progress
    this.emit({
      type: 'transferProgress',
      received: this.chunkCollector.receivedCount,
      total: this.chunkCollector.receivedCount + this.chunkCollector.getMissingIndices().length,
    });
  }

  private async handleSetTextEnd(id: string): Promise<void> {
    if (!this.channel || this.currentTransferId !== id || !this.chunkCollector) {
      return;
    }

    const result = this.chunkCollector.reassemble();

    // Send ack
    const ackMsg = createSetTextChunkedAckMessage(id, this.chunkCollector.receivedCount);
    await broadcastMessage(this.channel, ackMsg as unknown as Record<string, unknown>);

    if (result.success) {
      this.emit({ type: 'textReceived', text: result.text, bytes: result.totalBytes });
    } else {
      this.emit({ type: 'error', error: result.error || 'Chunk reassembly failed' });
    }

    // Clean up
    this.chunkCollector = null;
    this.currentTransferId = null;
  }

  // ---------------------------------------------------------------------------
  // Transport Commands (Phone → Reader)
  // ---------------------------------------------------------------------------

  /**
   * Send a transport command.
   *
   * @param command - The command to send
   * @param words - Number of words for rewind/fastForward
   */
  async sendCommand(command: TransportCommand, words?: number): Promise<void> {
    if (!this.isConnected || !this.channel) {
      throw new Error('Not connected');
    }

    let msg: Record<string, unknown>;

    switch (command) {
      case 'play':
        msg = createPlayMessage() as unknown as Record<string, unknown>;
        break;
      case 'pause':
        msg = createPauseMessage() as unknown as Record<string, unknown>;
        break;
      case 'rewind':
        msg = createRewindMessage(words) as unknown as Record<string, unknown>;
        break;
      case 'fastForward':
        msg = createFastForwardMessage(words) as unknown as Record<string, unknown>;
        break;
      case 'restart':
        msg = createRestartMessage() as unknown as Record<string, unknown>;
        break;
    }

    await broadcastMessage(this.channel, msg);
    await this.waitForAck(`transport:${command}`, ACK_TIMEOUT_MS);

    this.emit({ type: 'commandSent', command });
  }

  private async handleTransportCommand(command: TransportCommand, words?: number): Promise<void> {
    if (!this.channel) return;

    // Send ack first
    const ackMsg = createTransportAckMessage(command);
    await broadcastMessage(this.channel, ackMsg as unknown as Record<string, unknown>);

    // Emit received event
    this.emit({ type: 'commandReceived', command, words });
  }

  // ---------------------------------------------------------------------------
  // WPM Setting (Phone → Reader)
  // ---------------------------------------------------------------------------

  /**
   * Send WPM setting.
   *
   * @param wpm - Words per minute value
   */
  async sendWpm(wpm: number): Promise<void> {
    if (!this.isConnected || !this.channel) {
      throw new Error('Not connected');
    }

    const msg = createSetWpmMessage(wpm);
    await broadcastMessage(this.channel, msg as unknown as Record<string, unknown>);

    await this.waitForAck(`setWpm:${wpm}`, ACK_TIMEOUT_MS);

    this.emit({ type: 'wpmSent', wpm });
  }

  private async handleSetWpm(wpm: number): Promise<void> {
    if (!this.channel) return;

    // Send ack
    const ackMsg = createSetWpmAckMessage(wpm);
    await broadcastMessage(this.channel, ackMsg as unknown as Record<string, unknown>);

    // Emit received event
    this.emit({ type: 'wpmReceived', wpm });
  }

  // ---------------------------------------------------------------------------
  // Ack Handling
  // ---------------------------------------------------------------------------

  private waitForAck(key: string, timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pendingAcks.delete(key);
        reject(new Error(`Timeout waiting for ack: ${key}`));
      }, timeoutMs);

      this.pendingAcks.set(key, { resolve, reject, timeout });
    });
  }

  private resolveAck(key: string): void {
    const pending = this.pendingAcks.get(key);
    if (pending) {
      clearTimeout(pending.timeout);
      pending.resolve();
      this.pendingAcks.delete(key);
    }
  }

  private handleAck(msg: AckMessage): void {
    if (isHelloAckMessage(msg)) {
      this.resolveAck('hello');
      this.setState('connected');
    } else if (isSetTextAckMessage(msg)) {
      this.resolveAck(`setText:${msg.id}`);
    } else if (isSetTextChunkedAckMessage(msg)) {
      this.resolveAck(`setTextChunked:${msg.id}`);
    } else if (isTransportAckMessage(msg)) {
      this.resolveAck(`transport:${msg.cmd}`);
    } else if (isSetWpmAckMessage(msg)) {
      this.resolveAck(`setWpm:${msg.wpm}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Message Routing
  // ---------------------------------------------------------------------------

  private handleMessage(payload: Record<string, unknown>): void {
    try {
      // Route based on message type
      if (isHelloMessage(payload)) {
        this.handleHello(payload);
      } else if (isHelloAckMessage(payload) || isSetTextAckMessage(payload) ||
                 isSetTextChunkedAckMessage(payload) || isTransportAckMessage(payload) ||
                 isSetWpmAckMessage(payload)) {
        this.handleAck(payload as AckMessage);
      } else if (isSetTextMessage(payload)) {
        this.handleSetText(payload.id, payload.text, payload.totalBytes);
      } else if (isSetTextBeginMessage(payload)) {
        this.handleSetTextBegin(payload.id, payload.totalChunks, payload.totalBytes);
      } else if (isSetTextChunkMessage(payload)) {
        this.handleSetTextChunk(payload.id, payload.i, payload.chunk);
      } else if (isSetTextEndMessage(payload)) {
        this.handleSetTextEnd(payload.id);
      } else if (isPlayMessage(payload)) {
        this.handleTransportCommand('play');
      } else if (isPauseMessage(payload)) {
        this.handleTransportCommand('pause');
      } else if (isRewindMessage(payload)) {
        this.handleTransportCommand('rewind', payload.words);
      } else if (isFastForwardMessage(payload)) {
        this.handleTransportCommand('fastForward', payload.words);
      } else if (isRestartMessage(payload)) {
        this.handleTransportCommand('restart');
      } else if (isSetWpmMessage(payload)) {
        this.handleSetWpm(payload.wpm);
      }
    } catch (err) {
      console.error('Error handling pairing message:', err);
      this.emit({ type: 'error', error: err instanceof Error ? err.message : 'Unknown error' });
    }
  }
}

/**
 * Create a PairingManager for phone role.
 *
 * @param sessionCode - The pairing session code
 */
export function createPhonePairing(sessionCode: string): PairingManager {
  return new PairingManager('phone', sessionCode);
}

/**
 * Create a PairingManager for reader role.
 *
 * @param sessionCode - The pairing session code
 */
export function createReaderPairing(sessionCode: string): PairingManager {
  return new PairingManager('reader', sessionCode);
}
